import { useMutation, useQuery } from '@tanstack/react-query'
import {
	CheckCircle2,
	Database,
	Eye,
	EyeOff,
	Loader2,
	Pencil,
	XCircle,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { BranchEditDialog } from '@/components/branch-edit-dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet'
import {
	adminApi,
	type TestConnectionResult,
} from '@/lib/admin-api'
import { cn } from '@/utils/misc'

interface BranchDetailSheetProps {
	branchId: number | null
	onClose: () => void
}

export function BranchDetailSheet({ branchId, onClose }: BranchDetailSheetProps) {
	const [showPassword, setShowPassword] = useState(false)
	const [testResult, setTestResult] = useState<TestConnectionResult | null>(null)
	const [editOpen, setEditOpen] = useState(false)

	const branchQuery = useQuery({
		queryKey: ['admin', 'branches', branchId],
		queryFn: () => adminApi.branch(branchId!),
		enabled: branchId !== null,
	})

	const testMutation = useMutation({
		mutationFn: () => adminApi.testConnection(branchId!),
		onSuccess: (result) => setTestResult(result),
	})

	const branch = branchQuery.data

	const canTest = Boolean(
		branch?.fb_ipaddress &&
			branch?.fb_port &&
			branch?.fb_databasePathName &&
			branch?.fb_username &&
			branch?.fb_password,
	)

	const connectionUrl =
		branch?.fb_ipaddress && branch?.fb_port && branch?.fb_databasePathName
			? `${branch.fb_ipaddress}:${branch.fb_port}/${branch.fb_databasePathName}`
			: null

	function handleOpenChange(open: boolean) {
		if (!open) {
			onClose()
			setTestResult(null)
			setShowPassword(false)
			setEditOpen(false)
		}
	}

	return (
		<>
		<Sheet open={branchId !== null} onOpenChange={handleOpenChange}>
			<SheetContent className="sm:max-w-lg flex flex-col gap-0 p-0 overflow-hidden bg-white">
				<SheetHeader className="p-6 pb-4 border-b border-gray-100 shrink-0">
					<div className="flex items-start justify-between gap-3 pr-8">
						<div className="min-w-0">
							<SheetTitle className="text-gray-900">
								{branchQuery.isPending ? (
									<Skeleton className="h-6 w-44" />
								) : branch ? (
									`Branch #${branch.branchId}${branch.branchName ? ` — ${branch.branchName}` : ''}`
								) : (
									'Branch detail'
								)}
							</SheetTitle>
							<SheetDescription className="mt-1 text-gray-500">
								{branchQuery.isPending ? (
									<Skeleton className="h-4 w-24" />
								) : branch ? (
									`IČO ${branch.ico}`
								) : null}
							</SheetDescription>
						</div>
						{branch && (
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="shrink-0"
								onClick={() => setEditOpen(true)}
							>
								<Pencil className="h-3.5 w-3.5 mr-1.5" />
								Edit
							</Button>
						)}
					</div>
				</SheetHeader>

				<div className="flex-1 overflow-y-auto p-6 space-y-5">
					{branchQuery.isPending ? (
						<LoadingSkeleton />
					) : branchQuery.isError ? (
						<p className="text-sm text-red-600">Failed to load branch details.</p>
					) : branch ? (
						<>
							<Section title="Identification">
								<Field label="Branch ID" value={String(branch.branchId)} mono />
								<Field label="Name" value={branch.branchName} />
								<Field label="IČO" value={branch.ico} mono />
								<Field
									label="Department ID"
									value={
										branch.departmentId !== null
											? String(branch.departmentId)
											: null
									}
								/>
							</Section>

							<Section title="Firebird Connection">
								<Field label="IP Address" value={branch.fb_ipaddress} mono />
								<Field
									label="Port"
									value={branch.fb_port !== null ? String(branch.fb_port) : null}
									mono
								/>
								<Field
									label="Database Path"
									value={branch.fb_databasePathName}
									mono
								/>
								<Field label="Username" value={branch.fb_username} mono />
								<PasswordField
									value={branch.fb_password}
									show={showPassword}
									onToggle={() => setShowPassword((p) => !p)}
								/>
							</Section>

							<Section title="System">
								<Field label="Status">
									<StatusBadge connected={Boolean(branch.setupAt)} />
								</Field>
								<Field
									label="Setup at"
									value={branch.setupAt ? formatDate(branch.setupAt) : null}
								/>
								<Field label="Created at" value={formatDate(branch.createdAt)} />
								<Field label="Updated at" value={formatDate(branch.updatedAt)} />
							</Section>

							<Section title="Connection Test">
								{!canTest && (
									<p className="text-xs text-amber-600 pb-3">
										Missing Firebird credentials — cannot test.
									</p>
								)}
								<Button
									type="button"
									size="sm"
									variant="outline"
									disabled={!canTest || testMutation.isPending}
									onClick={() => {
										setTestResult(null)
										testMutation.mutate()
									}}
								>
									{testMutation.isPending ? (
										<>
											<Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
											Testing…
										</>
									) : (
										<>
											<Database className="h-3.5 w-3.5 mr-2" />
											Test Connection
										</>
									)}
								</Button>

								{(testMutation.isPending || testResult !== null) && (
									<TestResultPanel
										result={testResult}
										loading={testMutation.isPending}
										url={connectionUrl ?? ''}
									/>
								)}

								{testMutation.isError && (
									<p className="mt-2 text-xs text-red-600">
										Request failed:{' '}
										{testMutation.error instanceof Error
											? testMutation.error.message
											: 'Unknown error'}
									</p>
								)}
							</Section>
						</>
					) : null}
				</div>
			</SheetContent>
		</Sheet>

		{branch && (
			<BranchEditDialog
				branch={branch}
				open={editOpen}
				onClose={() => setEditOpen(false)}
			/>
		)}
	</>
	)
}

function Section({ title, children }: { title: string; children: ReactNode }) {
	return (
		<div>
			<h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
				{title}
			</h3>
			<div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 px-3">
				{children}
			</div>
		</div>
	)
}

function Field({
	label,
	value,
	mono,
	children,
}: {
	label: string
	value?: string | null
	mono?: boolean
	children?: ReactNode
}) {
	return (
		<div className="flex items-start gap-3 py-2.5">
			<span className="text-xs font-medium text-gray-500 uppercase tracking-wide shrink-0 w-[120px] pt-0.5">
				{label}
			</span>
			<div className="flex-1 min-w-0">
				{children ??
					(value != null ? (
						<span
							className={cn(
								'text-sm text-gray-900 break-all',
								mono && 'font-mono',
							)}
						>
							{value}
						</span>
					) : (
						<span className="text-sm text-gray-400">—</span>
					))}
			</div>
		</div>
	)
}

function PasswordField({
	value,
	show,
	onToggle,
}: {
	value: string | null
	show: boolean
	onToggle: () => void
}) {
	return (
		<div className="flex items-start gap-3 py-2.5">
			<span className="text-xs font-medium text-gray-500 uppercase tracking-wide shrink-0 w-[120px] pt-0.5">
				Password
			</span>
			<div className="flex items-center gap-2 flex-1 min-w-0">
				{value ? (
					<>
						<span className="text-sm font-mono text-gray-900 break-all">
							{show ? value : '••••••••'}
						</span>
						<button
							type="button"
							onClick={onToggle}
							className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
							aria-label={show ? 'Hide password' : 'Show password'}
						>
							{show ? (
								<EyeOff className="h-3.5 w-3.5" />
							) : (
								<Eye className="h-3.5 w-3.5" />
							)}
						</button>
					</>
				) : (
					<span className="text-sm text-gray-400">—</span>
				)}
			</div>
		</div>
	)
}

function StatusBadge({ connected }: { connected: boolean }) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
				connected ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700',
			)}
		>
			<span
				className={cn(
					'h-1.5 w-1.5 rounded-full',
					connected ? 'bg-green-500' : 'bg-amber-500',
				)}
			/>
			{connected ? 'Connected' : 'Pending'}
		</span>
	)
}

function TestResultPanel({
	result,
	loading,
	url,
}: {
	result: TestConnectionResult | null
	loading: boolean
	url: string
}) {
	return (
		<div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2 text-xs">
			<div className="flex items-start gap-2">
				<span className="font-medium text-gray-500 uppercase tracking-wide shrink-0 w-14 pt-px">
					URL
				</span>
				<code className="font-mono text-gray-700 break-all">{url}</code>
			</div>

			<div className="flex items-center gap-2">
				<span className="font-medium text-gray-500 uppercase tracking-wide shrink-0 w-14">
					Status
				</span>
				{loading ? (
					<span className="flex items-center gap-1 text-gray-500">
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
						Connecting…
					</span>
				) : result?.success ? (
					<span className="flex items-center gap-1 text-green-700 font-medium">
						<CheckCircle2 className="h-3.5 w-3.5" />
						Connected
					</span>
				) : (
					<span className="flex items-center gap-1 text-red-700 font-medium">
						<XCircle className="h-3.5 w-3.5" />
						Failed
					</span>
				)}
			</div>

			{result?.latencyMs !== null && result?.latencyMs !== undefined && (
				<div className="flex items-center gap-2">
					<span className="font-medium text-gray-500 uppercase tracking-wide shrink-0 w-14">
						Latency
					</span>
					<span className="text-gray-700">{result.latencyMs} ms</span>
				</div>
			)}

			{result?.error && (
				<div className="space-y-1">
					<span className="font-medium text-gray-500 uppercase tracking-wide block">
						Error
					</span>
					<pre className="text-red-700 whitespace-pre-wrap break-all font-mono bg-red-50 rounded p-2 leading-relaxed">
						{result.error}
					</pre>
				</div>
			)}

			{result?.testedAt && (
				<div className="flex items-center gap-2">
					<span className="font-medium text-gray-500 uppercase tracking-wide shrink-0 w-14">
						Tested at
					</span>
					<span className="text-gray-500">{formatDate(result.testedAt)}</span>
				</div>
			)}
		</div>
	)
}

function LoadingSkeleton() {
	return (
		<div className="space-y-5">
			{[4, 5, 4].map((count, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
				<div key={i} className="space-y-3">
					<Skeleton className="h-3 w-28" />
					<div className="rounded-lg border border-gray-200 px-3 py-1 space-y-2">
						{Array.from({ length: count }).map((_, j) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
							<div key={j} className="flex gap-3 py-1.5">
								<Skeleton className="h-3.5 w-24 shrink-0" />
								<Skeleton className="h-3.5 flex-1" />
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	)
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

